import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAdminData } from './AdminDataContext';
import { clearTimerDoc, startTimerDoc, subscribeTimer } from '../services/timerService';
import type { RunningTimer } from '../services/timerService';
import { debugErrorMessage, logFirebaseError } from '../lib/firebase';
import { StopTimerDialog } from '../components/admin/timer/StopTimerDialog';
import type { Case } from '../types/admin';
import { adminThemeClass, useAdminTheme } from '../hooks/useAdminTheme';
interface TimerApi {
  timer: RunningTimer | null;
  timerLoading: boolean;
  timerError: string;
  startTimer: (caseItem: Case) => Promise<void>;
  /** Åbner bekræftelsen, hvor tiden kan rettes og gemmes på sagen. */
  requestStop: () => void;
  /** Sletter timeren uden at gemme tiden. */
  discardTimer: () => Promise<boolean>;
}
const TimerContext = createContext<TimerApi | null>(null);

/** Arbejdstimeren følger den indloggede bruger og ligger i Firestore. */
export function TimerProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAdminData();
  const [timer, setTimer] = useState<RunningTimer | null>(null);
  const [timerLoading, setTimerLoading] = useState(true);
  const [timerError, setTimerError] = useState('');
  const [stopping, setStopping] = useState(false);
  const {
    theme
  } = useAdminTheme();
  useEffect(() => {
    if (!user) {
      setTimer(null);
      setTimerLoading(false);
      return;
    }
    setTimerLoading(true);
    return subscribeTimer(user.uid, next => {
      setTimer(next);
      setTimerLoading(false);
      setTimerError('');
    }, error => {
      logFirebaseError('onSnapshot adminTimers', error);
      setTimerLoading(false);
      setTimerError(debugErrorMessage('adminTimers', error));
    });
  }, [user]);
  const requestStop = useCallback(() => setStopping(true), []);
  const startTimer = useCallback(async (caseItem: Case) => {
    if (!user) return;
    if (timer) {
      if (timer.caseId === caseItem.id) return;
      // Kun én timer ad gangen — den første skal stoppes og gemmes først.
      if (window.confirm(`Der kører allerede en timer på "${timer.caseTitle}". Vil du stoppe den først?`)) {
        setStopping(true);
      }
      return;
    }
    try {
      setTimerError('');
      await startTimerDoc(user.uid, caseItem.id, caseItem.title);
    } catch (error) {
      logFirebaseError('startTimer', error);
      setTimerError(debugErrorMessage('startTimer', error));
    }
  }, [timer, user]);
  const discardTimer = useCallback(async () => {
    if (!user) return false;
    try {
      await clearTimerDoc(user.uid);
      return true;
    } catch (error) {
      logFirebaseError('discardTimer', error);
      setTimerError(debugErrorMessage('discardTimer', error));
      return false;
    }
  }, [user]);
  const value = useMemo(() => ({
    timer,
    timerLoading,
    timerError,
    startTimer,
    requestStop,
    discardTimer
  }), [timer, timerLoading, timerError, startTimer, requestStop, discardTimer]);
  return <TimerContext.Provider value={value} data-id="element-883" data-mp="pibggfcp9wax">
      {children}
      {/* Samme tema som admin, så vinduet får de rigtige farver. */}
      <div className={`${adminThemeClass(theme)} font-sans text-ink`} data-id="element-884" data-mp="uav46553q7tg">
        <StopTimerDialog open={stopping && Boolean(timer)} timer={timer} onClose={() => setStopping(false)} data-id="element-885" data-mp="z84rnf8xk9en" />
      </div>
    </TimerContext.Provider>;
}
export function useTimer(): TimerApi {
  const context = useContext(TimerContext);
  if (!context) throw new Error('useTimer skal bruges inde i TimerProvider');
  return context;
}